import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { PanelModule } from 'primeng/panel';
import { PopoverModule } from 'primeng/popover';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';

export interface GridColumn {
  field: string;
  header: string;
  isSortable?: boolean;
  customRender?: (row: any) => string;
}
@Component({
  selector: 'app-list',
  imports: [
        CommonModule,
        TableModule,
        TooltipModule,
        PanelModule,
        RouterModule,
        ButtonModule,
        PopoverModule
  ],
  templateUrl: './list.component.html',
  styleUrl: './list.component.scss'
})
export class ListComponent {
  @Input() data: any[] = [];
  @Input() columns: any[] = [];
  @Input() rows: number = 10;
  @Input() rowsPerPageOptions: number[] = [10, 25, 50];
  @Input() loading: boolean = false;
  @Input() totalRecords: number = 0;

  @Output() nameClick = new EventEmitter<any>();
  @Output() pageChange = new EventEmitter<any>();
  @Output() sortChange = new EventEmitter<any>();

  // Handle name click event with proper error handling
  onNameClick(rowData: any, rowIndex: number): void {
    try {
      
      // Validate input data
      if (!rowData) {
        console.error('Row data is null or undefined');
        return;
      }

      // Create a deep copy to prevent modification of original data
      const dataCopy = this.createDeepCopy(rowData);
      
      // Emit the event with copied data
      this.nameClick.emit({
        data: dataCopy,
        index: rowIndex,
        original: rowData // Keep reference to original for debugging
      });
    } catch (error) {
      console.error('Error: ', error);
    }
  }

  // Create a proper deep copy of the data
  private createDeepCopy(obj: any): any {
    try {
      if (obj === null || obj === undefined) {
        return obj;
      }
      
      if (typeof obj !== 'object') {
        return obj;
      }
      
      // Handle arrays
      if (Array.isArray(obj)) {
        return obj.map(item => this.createDeepCopy(item));
      }
      
      // Handle objects
      const copy: any = {};
      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          copy[key] = this.createDeepCopy(obj[key]);
        }
      }
      
      return copy;
    } catch (error) {
      console.error('Error creating deep copy:', error);
      // Fallback to JSON method
      return JSON.parse(JSON.stringify(obj));
    }
  }

  // Method to handle object values and display proper text
  getDisplayValue(value: any): string {
    try {
      // Handle null or undefined values
      if (value === null || value === undefined) {
        return '--';
      }

      // Handle objects (like title, status, etc.)
      if (typeof value === 'object' && value !== null) {
        // Check for common object structures
        if (value.value !== undefined) {
          return value.value;
        }
        if (value.name !== undefined) {
          return value.name;
        }
        if (value.description !== undefined) {
          return value.description;
        }
        if (value.label !== undefined) {
          return value.label;
        }
        // If object has code property but no display property, return code
        if (value.code !== undefined) {
          return value.code;
        }
        // Fallback to string conversion
        return JSON.stringify(value);
      }

      // Handle empty strings or strings with just dashes
      if (value === '' || value === '--') {
        return '--';
      }

      // Handle regular string/number values
      return String(value);
    } catch (error) {
      console.error('Error in getDisplayValue:', error, 'Value:', value);
      return '--';
    }
  }
}
